import { Injectable } from '@angular/core';

export type ProductCatalogCacheConfig = {
    readonly namespace: string;
    readonly name: string;
    readonly ttlMs: number;
    readonly params?: Record<string, unknown>;
    readonly userScope?: string | number | null;
    readonly clientScope?: string | number | null;
};

type ProductCatalogCacheEntry<T> = {
    readonly cachedAt: number;
    readonly value: T;
};

/** Shared in-memory cache for heavy product catalog requests. */
@Injectable({ providedIn: 'root' })
export class ProductCatalogCacheService {
    private readonly cache = new Map<string, ProductCatalogCacheEntry<unknown>>();

    private readonly pending = new Map<string, Promise<unknown>>();

    public getOrLoad<T>(config: ProductCatalogCacheConfig, loader: () => Promise<T>): Promise<T> {
        const key = this.buildCacheKey(config);
        const cached = this.cache.get(key) as ProductCatalogCacheEntry<T> | undefined;

        if (cached && Date.now() - cached.cachedAt <= config.ttlMs) {
            return Promise.resolve(this.cloneValue(cached.value));
        }

        const pendingRequest = this.pending.get(key) as Promise<T> | undefined;

        if (pendingRequest) {
            return pendingRequest.then((value) => this.cloneValue(value));
        }

        const request = loader()
            .then((value) => {
                this.cache.set(key, {
                    cachedAt: Date.now(),
                    value: this.cloneValue(value),
                });

                return this.cloneValue(value);
            })
            .finally(() => {
                this.pending.delete(key);
            });

        this.pending.set(key, request);

        return request;
    }

    public clear(config: ProductCatalogCacheConfig): void {
        const key = this.buildCacheKey(config);

        this.cache.delete(key);
        this.pending.delete(key);
    }

    private buildCacheKey(config: ProductCatalogCacheConfig): string {
        return [
            config.namespace,
            config.name,
            String(config.userScope ?? 'all-users'),
            String(config.clientScope ?? 'all-clients'),
            this.stringifyParams(config.params ?? {}),
        ].join(':');
    }

    private stringifyParams(params: Record<string, unknown>): string {
        return JSON.stringify(this.sortRecord(params));
    }

    private sortRecord(params: Record<string, unknown>): Record<string, unknown> {
        return Object.keys(params)
            .sort()
            .reduce<Record<string, unknown>>((sorted, key) => {
                sorted[key] = params[key];

                return sorted;
            }, {});
    }

    private cloneValue<T>(value: T): T {
        if (Array.isArray(value)) {
            return [...value] as T;
        }

        if (value && typeof value === 'object') {
            return { ...(value as Record<string, unknown>) } as T;
        }

        return value;
    }
}
